import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Rating,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';

const RecyclingQuality: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState<any>(null);
  const [formData, setFormData] = useState({
    materialType: '',
    batchNumber: '',
    inspector: '',
    inspectionDate: '',
    qualityScore: 0,
    contaminationLevel: '',
    moistureContent: '',
    sortingAccuracy: '',
    notes: '',
  });

  // Mock data for demonstration
  const qualityChecks = [
    {
      id: '1',
      materialType: 'PET Bottles',
      batchNumber: 'PET-2024-001',
      inspector: 'John Smith',
      inspectionDate: '2024-03-20',
      qualityScore: 4.5,
      contaminationLevel: '2%',
      moistureContent: '0.5%',
      sortingAccuracy: '98%',
      status: 'Passed',
      notes: 'Excellent sorting quality',
    },
    // Add more mock quality checks as needed
  ];

  const materialTypes = [
    'PET Bottles',
    'HDPE Containers',
    'Mixed Paper',
    'Cardboard',
    'Aluminum Cans',
    'Glass Bottles',
  ];

  const handleOpen = (quality?: any) => {
    if (quality) {
      setSelectedQuality(quality);
      setFormData({
        materialType: quality.materialType,
        batchNumber: quality.batchNumber,
        inspector: quality.inspector,
        inspectionDate: quality.inspectionDate,
        qualityScore: quality.qualityScore,
        contaminationLevel: quality.contaminationLevel,
        moistureContent: quality.moistureContent,
        sortingAccuracy: quality.sortingAccuracy,
        notes: quality.notes,
      });
    } else {
      setSelectedQuality(null);
      setFormData({
        materialType: '',
        batchNumber: '',
        inspector: '',
        inspectionDate: '',
        qualityScore: 0,
        contaminationLevel: '',
        moistureContent: '',
        sortingAccuracy: '',
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQuality(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        icon={<VerifiedIcon />}
        title={t('recyclingPages.quality.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('recyclingPages.quality.newQualityCheck')}
          </Button>
        }
      />

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.materialType')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.batch')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.inspector')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.date')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.qualityScore')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.contamination')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.quality.moisture')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('recyclingPages.quality.sortingAccuracy')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {qualityChecks.map((check) => (
                <TableRow 
                  key={check.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {check.materialType}
                    </Typography>
                  </TableCell>
                  <TableCell>{check.batchNumber}</TableCell>
                  <TableCell>{check.inspector}</TableCell>
                  <TableCell>{check.inspectionDate}</TableCell>
                  <TableCell>
                    <Rating 
                      value={check.qualityScore} 
                      precision={0.5} 
                      readOnly 
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{check.contaminationLevel}</TableCell>
                  <TableCell>{check.moistureContent}</TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={parseInt(check.sortingAccuracy)} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {check.sortingAccuracy}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={check.status}
                      value={check.status}
                      icon={
                        check.status === 'Passed' ? <CheckCircleIcon /> :
                        check.status === 'Warning' ? <WarningIcon /> :
                        <ErrorIcon />
                      }
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleOpen(check)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {selectedQuality ? t('recyclingPages.quality.editDialogTitle') : t('recyclingPages.quality.addDialogTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.materialType')}</InputLabel>
                  <Select
                    value={formData.materialType}
                    onChange={(e) => setFormData({ ...formData, materialType: e.target.value })}
                    label={t('common.materialType')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {materialTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.batchNumber')}
                  value={formData.batchNumber}
                  onChange={(e) => setFormData({ ...formData, batchNumber: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.inspector')}
                  value={formData.inspector}
                  onChange={(e) => setFormData({ ...formData, inspector: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.inspectionDate')}
                  type="date"
                  value={formData.inspectionDate}
                  onChange={(e) => setFormData({ ...formData, inspectionDate: e.target.value })}
                  required
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography component="legend">{t('processing.quality.qualityScore')}</Typography>
                <Rating
                  value={formData.qualityScore}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, qualityScore: newValue || 0 });
                  }}
                  precision={0.5}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.contaminationLevel')}
                  value={formData.contaminationLevel}
                  onChange={(e) => setFormData({ ...formData, contaminationLevel: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.quality.contaminationPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('processing.quality.moistureContent')}
                  value={formData.moistureContent}
                  onChange={(e) => setFormData({ ...formData, moistureContent: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.quality.moisturePlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('recyclingPages.quality.sortingAccuracy')}
                  value={formData.sortingAccuracy}
                  onChange={(e) => setFormData({ ...formData, sortingAccuracy: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('recyclingPages.quality.accuracyPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedQuality ? t('common.update') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default RecyclingQuality; 